import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { memo, useCallback, useState } from 'react';
import type { SectionCoverage } from '../collapsed-table/types';
import { PlanCellValue } from '../plan-cell-value';
import * as SX from './sx';
import type { CollapsibleSectionProps as Props } from './types';

function SectionHeaderPreview({ coverage }: { coverage?: SectionCoverage }) {
  if (!coverage || coverage.total === 0 || coverage.filled === 0) {
    return <PlanCellValue value="dash" />;
  }
  const label = coverage.filled === coverage.total ? 'Full Access' : 'Limited';
  return (
    <Stack sx={SX.coverageStack}>
      <Typography variant="body2" color="grey.700" noWrap>
        {label}
      </Typography>
      <Typography variant="body5" color="grey.500" noWrap>
        {coverage.filled} of {coverage.total} features
      </Typography>
    </Stack>
  );
}

export const CollapsibleSection = memo(({ section, products }: Props) => {
  const [open, setOpen] = useState(false);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  return (
    <>
      <TableRow sx={SX.sectionHeaderRow(open)} onClick={handleToggle}>
        <TableCell sx={SX.sectionHeaderCell}>
          <IconButton size="small" sx={SX.collapseButton}>
            {open ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
          </IconButton>
          <Typography variant="body2" fontWeight={500}>
            {section.title}
          </Typography>
        </TableCell>
        {products.map((product) => (
          <TableCell
            key={product.id}
            align="center"
            sx={SX.sectionHeaderValueCell}
          >
            <SectionHeaderPreview
              coverage={section.headerCoverage[product.id]}
            />
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        <TableCell sx={SX.collapsibleWrapperCell} colSpan={products.length + 1}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Table size="small">
              <TableBody>
                {section.features.map((row) => (
                  <TableRow key={row.feature} sx={SX.featureRow}>
                    <TableCell sx={SX.featureNameCell}>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        color="text.secondary"
                      >
                        {row.feature}
                      </Typography>
                    </TableCell>
                    {products.map((product) => (
                      <TableCell
                        key={product.id}
                        align="center"
                        sx={SX.featureValueCell}
                      >
                        <PlanCellValue
                          value={row.values[product.id] ?? 'dash'}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
});

CollapsibleSection.displayName = 'CollapsibleSection';
